import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Briefcase, GridFour, LineSegment, Plus, ShieldCheck} from '../../icons';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {Spinner} from '../../Spinner';
import {
    AUDIENCE_ADMIN,
    AUDIENCE_MEMBER,
    audienceOf,
    CHANNEL_WEBHOOK,
    clientChoices,
    controlsSummary,
    filterGroups,
    GROUP_AGENCY,
    GROUP_CLIENT,
    groupAlerts,
    groupRows,
    isActiveRule,
    isControlRule,
    isRuleOn,
    matchesClient,
    matchesStatus,
    partition,
    resolveRuleParam,
    ruleAnchorId,
    ruleChannels,
    SCOPE_AGENCY,
    STATUS_ACTIVE,
    STATUS_ALL,
    STATUS_FILTERS,
    STATUS_PAUSED,
    teamActionOf,
} from '../rulesModel';
import {exceptionLineCount} from '../ruleText';
import {visualOnlyColumnFor} from '../legacyLinks';
import {
    accountsRulePath,
    ALERTS_ACCOUNTS,
    ALERTS_NEW,
    columnGearPath,
    PARAM_PAUSE,
    PARAM_RULE,
    suggestionPath,
} from '../alertsCenterPaths';
import {AlertRowV2} from './RuleRow';
import {ChipMenu, ChipOptions} from '../builder/ChipMenu';
import {WebhookStatusInline} from './WebhookStatusCard';
import {WebhookSetupDrawer} from './WebhookSetupDrawer';
import {WebhookCardV2} from './WebhookCardV2';
import {connectedWebhook, eventsForSave, rowWebhookEvents, webhookStatus} from './webhookModel';
import {WEBHOOK_OK} from './ChannelsCard';
import {useRuleWrites} from './useRuleWrites';

const HIGHLIGHT_MS = 2500;
// «Le llegan a: Ti» se filtra con lo que ya trae la lista (`reaches_viewer` de quien mira), sin pedirla de nuevo.
const PERSON_ME = 'me';

// `rowWebhookEvents` vive en el modelo del webhook (sin React); se re-exporta para quien la importaba de acá.
export {rowWebhookEvents};

const GROUP_ICONS = {[GROUP_AGENCY]: GridFour, [GROUP_CLIENT]: Briefcase};
const STATUS_LABEL_KEYS = {
    [STATUS_ALL]: 'alerts_center.mr.status_all',
    [STATUS_ACTIVE]: 'alerts_center.mr.status_active',
    [STATUS_PAUSED]: 'alerts_center.mr.status_paused',
};

/**
 * La franja de arriba de «Mis reglas» (UD-18): los avisos de las cuentas viven en su página; acá se dice que existen,
 * cuántos están activos y cuántos le llegan a quien mira (o a la persona del filtro). La marca de deploy es
 * `data-testid="alc-watch"`.
 */
export function WatchStrip({controls, person = null}) {
    const {t} = useTranslation();
    const summary = controlsSummary(controls);
    if (!summary.total) return null;
    // Nombra tres (una página caída, una cuenta que no imprime, el saldo) y cuenta el resto.
    const more = Math.max(0, summary.total - 3);
    const text = more > 0 ? t('alerts_center.mr.watch_text_more', {count: more}) : t('alerts_center.mr.watch_text');
    const reach = person
        ? t('alerts_center.mr.watch_person', {count: summary.mine, name: person.name, interpolation: {escapeValue: false}})
        : t('alerts_center.mr.watch_mine', {count: summary.mine});
    return (
        <section className="alc-watch" aria-labelledby="alc-watch-title" data-testid="alc-watch">
            <span className="alc-watch__icon" aria-hidden="true"><ShieldCheck size={22} weight="duotone"/></span>
            <h2 id="alc-watch-title" className="alc-watch__title">{t('alerts_center.mr.watch_title')}</h2>
            <p className="alc-watch__text">
                {text}{' '}
                <span className="alc-watch__meta" data-testid="alc-watch-counts">
                    {t('alerts_center.mr.watch_on', {on: summary.on, count: summary.total})} · {reach}
                </span>
            </p>
            <Link to={ALERTS_ACCOUNTS} className="alc-btn -ghost alc-watch__cta" data-testid="alc-watch-cta">
                {t('alerts_center.mr.watch_cta')}
            </Link>
        </section>
    );
}

/**
 * «Mis reglas» de una agencia con las alertas de Dashbo convertidas (`system_alerts_converted: true`), según UD-18
 * (24/10, `SSOT/ux/decisiones.md`; mockup en `~/.claude/plans/2026-10-24-mis-reglas-ud-18/`):
 *
 * - Arriba, la franja de los avisos de las cuentas (`WatchStrip`): esos avisos tienen su propia página
 *   (`/alerts/rules/cuentas`) y salen de esta lista.
 * - «Tus alertas», agrupadas por alcance: toda la agencia, un cliente, una línea. La configuración propia de una línea
 *   (una de Dashbo con `@` en la clave) cuelga de su alerta como «Excepción en la línea …»; si su alerta no tiene fila
 *   (no debería pasar), va sola al grupo «Una línea» (`groupAlerts`).
 * - Filtros: Todas / Activas / Pausadas (con conteos), «Le llegan a» (el admin: «Ti» con lo que ya trae la lista, otra
 *   persona con `?as_user=`, `onViewAs`) y «Cliente» (las de toda la agencia se ven con cualquiera). Una alerta que no
 *   pasa pero tiene una excepción que sí, queda atenuada como contexto.
 * - A la derecha de los filtros, el webhook en una línea (el admin), con el panel de 3 pasos.
 * - Al pie, «Dashbo tiene N alertas sugeridas…» lleva a «Crear una alerta», donde están las sugeridas.
 * - Un no admin ve la misma lista, sin «Le llegan a» ni el webhook; lo de la agencia, en sólo lectura (D13).
 * - `?regla=` resalta la fila (por id o por el indicador de la pantalla vieja); un aviso de control lleva a su página,
 *   una sugerida a «Crear una alerta» y una que sólo pinta al Tablero con la tuerca de su columna. `?pausar=1` (el link
 *   de un correo) pregunta en la fila.
 *
 * La marca de deploy es `data-testid="alc-v2-list"` y, de UD-18, `alc-watch`.
 */
export function ConvertedRulesList({payload, loadError, onRetry, onReload, onPayloadChange, userId, webhook,
                                       onWebhookSaved, onWebhookRetry, canEdit = true, me = null, viewAs = null,
                                       onViewAs = null, team = null, viewAsPending = false, teamError = false,
                                       onRetryTeam = null, options = null, freshId = null}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const ruleParam = searchParams.get(PARAM_RULE);
    const pauseParam = searchParams.get(PARAM_PAUSE) === '1';
    const [status, setStatus] = useState(STATUS_ALL);
    const [clientId, setClientId] = useState('');
    const [onlyMine, setOnlyMine] = useState(false);
    const [highlightId, setHighlightId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pauseAskId, setPauseAskId] = useState(null);
    const highlightTimer = useRef(null);
    const pauseRef = useRef(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(highlightTimer.current);
        };
    }, []);

    const {alerts, controls, suggestions} = useMemo(() => partition(payload), [payload]);
    const rows = useMemo(() => [...alerts, ...controls], [alerts, controls]);
    const webhookState = webhookStatus(payload && payload.webhook, webhook);
    const findRow = useCallback((id) => rows.find((item) => String(item.id) === String(id)) || null, [rows]);
    const reload = onReload || onRetry;

    // Quién mira: `me` lo trae la pantalla (el `viewer` de la lista sin «Le llegan a»); si todavía no, el de la lista.
    const viewer = me || (payload && !viewAs ? payload.viewer : null) || null;
    const audience = audienceOf(viewer);
    const admin = audience === AUDIENCE_ADMIN;
    const member = audience === AUDIENCE_MEMBER;
    const viewerId = viewer && viewer.id !== undefined ? viewer.id : userId;
    const person = admin && viewAs ? viewAs : null;
    const limits = payload ? payload.limits : null;

    const highlight = useCallback((id) => {
        clearTimeout(highlightTimer.current);
        setHighlightId(String(id));
        highlightTimer.current = setTimeout(() => {
            if (mounted.current) setHighlightId(null);
        }, HIGHLIGHT_MS);
    }, []);

    const writes = useRuleWrites({onPayloadChange, reload, person, limits, onRestored: (rule) => highlight(rule.id)});

    // Al cambiar de persona en el filtro, lo abierto era de la vista anterior.
    const viewAsId = viewAs ? String(viewAs.id) : null;
    useEffect(() => {
        setDrawerOpen(false);
        setPauseAskId(null);
    }, [viewAsId]);

    // ------------------------------------------------------------------ filtros ---------------------
    const reachesShown = (rule) => Boolean(rule.delivery && rule.delivery.reaches_viewer === true);
    const byPerson = (rule) => (person || onlyMine ? reachesShown(rule) : true);
    const baseTest = (rule) => byPerson(rule) && matchesClient(rule, clientId);
    const groups = useMemo(() => groupAlerts(alerts), [alerts]);
    const pool = groupRows(groups).filter(baseTest);
    const counts = {
        [STATUS_ALL]: pool.length,
        [STATUS_ACTIVE]: pool.filter(isRuleOn).length,
        [STATUS_PAUSED]: pool.filter((rule) => !isRuleOn(rule)).length,
    };
    const shown = filterGroups(groups, (rule) => baseTest(rule) && matchesStatus(rule, status));
    // La primera alerta a la vista lleva las anclas de la guía `aprende-alertas` (D7): una sola fila marcada.
    const guideRowId = shown.length ? String(shown[0].entries[0].rule.id) : null;
    const filtering = status !== STATUS_ALL || clientId !== '' || onlyMine || Boolean(person);
    const clearFilters = () => {
        setStatus(STATUS_ALL);
        setClientId('');
        setOnlyMine(false);
        if (person && onViewAs) onViewAs(null);
    };

    // «Pausada en la agencia; sigue activa en 2 líneas»: de las excepciones de verdad, no de las que pasan el filtro.
    const parentNotes = useMemo(() => {
        const notes = new Map();
        groups.forEach((group) => group.entries.forEach((entry) => {
            if (!entry.exceptions.length || isRuleOn(entry.rule)) return;
            const lines = entry.exceptions.filter(isRuleOn).reduce((sum, rule) => sum + Math.max(1, exceptionLineCount(rule)), 0);
            if (lines > 0) notes.set(String(entry.rule.id), lines);
        }));
        return notes;
    }, [groups]);

    // «Avisan en cualquiera de las 62 líneas»: las de toda la agencia de Dashbo dicen cuántas mira cada una.
    const agencyLines = alerts
        .filter((rule) => rule.scope && rule.scope.type === SCOPE_AGENCY && rule.stats && typeof rule.stats.lines_in_scope === 'number')
        .reduce((max, rule) => Math.max(max, rule.stats.lines_in_scope), -1);

    // ------------------------------------------------------------------ resaltar y llevar a la vista --
    useEffect(() => {
        if (highlightId === null) return;
        const target = document.getElementById(ruleAnchorId(highlightId));
        if (target && target.scrollIntoView) target.scrollIntoView({block: 'center', behavior: 'smooth'});
    }, [highlightId]);

    // `?regla=`: una vez por valor (cerrar o cambiar algo no lo vuelve a disparar).
    const handledParam = useRef(null);
    useEffect(() => {
        if (!ruleParam || !payload || handledParam.current === ruleParam) return;
        handledParam.current = ruleParam;
        const target = resolveRuleParam(ruleParam, {alerts, controls, suggestions});
        if (target && target.kind === 'row') {
            const row = findRow(target.id);
            // Un aviso de control vive en «Avisos de las cuentas»: allá se resalta (y se pregunta, con `?pausar=1`).
            if (row && isControlRule(row)) {
                navigate(`${accountsRulePath(target.id)}${pauseParam ? `&${PARAM_PAUSE}=1` : ''}`, {replace: true});
                return;
            }
            // La fila tiene que estar a la vista: los filtros se limpian.
            setStatus(STATUS_ALL);
            setClientId('');
            setOnlyMine(false);
            highlight(target.id);
            if (pauseParam && row) setPauseAskId(String(target.id));
            return;
        }
        if (target && target.kind === 'suggestion') {
            navigate(suggestionPath(target.key), {replace: true});
            return;
        }
        // Una que sólo pinta ya no es una fila: se ajusta en la tuerca de su columna del Tablero.
        const column = visualOnlyColumnFor(ruleParam);
        if (column) navigate(columnGearPath(column), {replace: true});
    }, [ruleParam, pauseParam, payload, alerts, controls, suggestions, findRow, highlight, navigate]);

    // `#webhook` (el link que deja Plataformas): el estado del webhook se dibuja con la lista, el navegador no lo encuentra.
    const handledHash = useRef(false);
    useEffect(() => {
        if (handledHash.current || !payload || window.location.hash !== '#webhook') return;
        handledHash.current = true;
        const card = document.getElementById('webhook');
        if (!card) return;
        if (card.scrollIntoView) card.scrollIntoView({block: 'start', behavior: 'smooth'});
        card.focus({preventScroll: true});
    }, [payload]);

    // ------------------------------------------------------------------ `?pausar=1` ------------------
    const dismissPause = () => {
        setPauseAskId(null);
        const next = new URLSearchParams(searchParams);
        next.delete(PARAM_PAUSE);
        setSearchParams(next, {replace: true});
    };

    useEffect(() => {
        if (pauseAskId === null || !pauseRef.current) return;
        const button = pauseRef.current.querySelector('button');
        if (button) button.focus();
    }, [pauseAskId]);

    // La pregunta del link de un correo: dejar de recibirla (si es de equipo y le llega) o pausarla para todos (si quien
    // mira puede).
    const renderPauseConfirm = (rule) => {
        const action = teamActionOf(rule);
        const canPause = canEdit && (typeof rule.can_pause === 'boolean' ? rule.can_pause : rule.can_edit !== false);
        return (
            <div className="alc-confirm alc-v2-pause" role="alertdialog" aria-labelledby="alc-v2-pause-title"
                 data-testid="alc-pause-confirm" ref={pauseRef}>
                <p id="alc-v2-pause-title" className="alc-confirm__text">{t('alerts_center.row.pause_question_r5')}</p>
                <div className="alc-confirm__acts">
                    {action && action.next && (
                        <button type="button" className="alc-btn -primary" onClick={() => { writes.optOut(rule, true); dismissPause(); }}>
                            {t('alerts_center.row.stop_receiving')}
                        </button>
                    )}
                    {canPause && isRuleOn(rule) && (
                        <button type="button" className="alc-btn -ghost"
                                onClick={() => { writes.toggleActive(rule, false); dismissPause(); }}>
                            {t('alerts_center.row.pause_for_everyone_r5')}
                        </button>
                    )}
                    <button type="button" className="alc-btn -ghost" onClick={dismissPause}>{t('alerts_center.row.pause_keep')}</button>
                </div>
            </div>
        );
    };

    // ------------------------------------------------------------------ webhook ---------------------
    // Los eventos de alertas salen de las filas tal como están ahora, no de la foto que se leyó al cargar
    // (review C-av2-fe-centro-03).
    const governedEvents = rowWebhookEvents(rows);
    const rowsWithWebhook = rowWebhookEvents(rows.filter((rule) => isActiveRule(rule) && ruleChannels(rule).includes(CHANNEL_WEBHOOK)));
    const knownEvents = webhook && webhook.status === WEBHOOK_OK
        ? eventsForSave(webhook.selected || [], {governed: governedEvents, on: rowsWithWebhook}) : null;

    // Al cerrar el panel el foco vuelve al estado del webhook, que es quien lo abrió (C-av2-fe-centro-19).
    const focusAfterDrawer = useRef(false);
    const closeDrawer = () => {
        focusAfterDrawer.current = true;
        setDrawerOpen(false);
    };
    useEffect(() => {
        if (drawerOpen || !focusAfterDrawer.current) return;
        focusAfterDrawer.current = false;
        // Si el panel ya se lo devolvió a quien lo abrió (o el usuario se fue a otro lado), no se toca.
        const active = document.activeElement;
        if (active && active !== document.body) return;
        const card = document.getElementById('webhook');
        if (card && card.focus) card.focus();
    });

    const webhookConnected = ({url, destination, webhook: saved}) => {
        // El `webhook` que devolvió el PUT manda; si no vino, conectado (sin tocar la pausa) y se recarga: guardar
        // la misma URL no despausa (review C-av2-fe-centro-04).
        onPayloadChange((current) => ({...current, webhook: saved || connectedWebhook(current && current.webhook, destination)}));
        if (!saved && reload) reload();
        if (webhook && webhook.status === WEBHOOK_OK) onWebhookSaved({...webhook, url, selected: knownEvents || webhook.selected});
        else if (onWebhookRetry) onWebhookRetry();
        closeDrawer();
    };

    // ------------------------------------------------------------------ dibujo ----------------------
    const renderRow = (rule, extra = {}) => {
        const id = String(rule.id);
        return (
            <AlertRowV2 key={id} rule={rule} anchorId={ruleAnchorId(rule.id)} viewerId={viewerId} canEditPage={canEdit}
                        busy={writes.isBusy(rule.id)} fresh={freshId !== null && String(freshId) === id}
                        highlighted={highlightId === id} guideRow={guideRowId === id}
                        notice={pauseAskId === id ? renderPauseConfirm(rule) : null}
                        team={team} person={person} onToggleActive={writes.toggleActive} onOptOut={writes.optOut}
                        onReturnToAgency={writes.returnToAgency} onRemove={writes.remove} {...extra}/>
        );
    };

    const renderEntry = (entry) => {
        const lines = parentNotes.get(String(entry.rule.id));
        const parentNote = lines ? t('alerts_center.mr.parent_note', {count: lines}) : null;
        if (!entry.exceptions.length) return renderRow(entry.rule, {context: entry.context, orphan: entry.orphan, parentNote});
        return (
            <div key={`grp-${entry.rule.id}`} className="alc-mr-grp" data-testid="alc-mr-family">
                {renderRow(entry.rule, {context: entry.context, parentNote})}
                {entry.exceptions.map((rule) => renderRow(rule, {exception: true, parent: entry.rule}))}
            </div>
        );
    };

    const groupDesc = (key) => {
        if (key !== GROUP_AGENCY) return t(`alerts_center.mr.group_${key}_desc`);
        return agencyLines >= 0 ? t('alerts_center.mr.group_agency_desc_count', {count: agencyLines})
            : t('alerts_center.mr.group_agency_desc');
    };

    const renderGroup = (group) => {
        const Icon = GROUP_ICONS[group.key] || LineSegment;
        const titleId = `alc-mr-group-${group.key}`;
        return (
            <section key={group.key} className="alc-mr-group" aria-labelledby={titleId} data-testid={titleId}>
                <header className="alc-mr-group__head">
                    <h3 id={titleId} className="alc-mr-group__name">
                        <Icon size={16} aria-hidden="true"/>
                        {t(`alerts_center.mr.group_${group.key}`)}
                    </h3>
                    <span className="alc-mr-group__desc">{groupDesc(group.key)}</span>
                </header>
                <div className="alc-mr-card">{group.entries.map(renderEntry)}</div>
            </section>
        );
    };

    if (loadError !== null && loadError !== undefined) {
        return (
            <LoadErrorState title={loadError} text={t('alerts_center.load_error_explanation')}
                            retryLabel={t('alerts_center.retry')} onRetry={onRetry}/>
        );
    }

    // «Le llegan a» (el admin): cualquiera, «Ti» o una persona del equipo.
    const personOptions = [
        {value: '', label: t('alerts_center.mr.person_any')},
        {value: PERSON_ME, label: t('alerts_center.mr.person_me')},
        ...(Array.isArray(team) ? team : [])
            .filter((user) => !viewer || String(user.id) !== String(viewer.id))
            .map((user) => ({value: String(user.id), label: user.name || user.email})),
    ];
    const personValue = person ? String(person.id) : (onlyMine ? PERSON_ME : '');
    const choosePerson = (value) => {
        setOnlyMine(value === PERSON_ME);
        if (!onViewAs) return;
        if (!value || value === PERSON_ME) {
            if (viewAs) onViewAs(null);
            return;
        }
        const user = (team || []).find((item) => String(item.id) === value);
        if (user) onViewAs({id: user.id, name: user.name || user.email});
    };
    const personLabel = (personOptions.find((option) => option.value === personValue) || personOptions[0]).label;

    const clients = clientChoices(alerts, options);
    const clientOptions = [{value: '', label: t('alerts_center.mr.client_all')},
        ...clients.map((client) => ({value: client.id, label: client.name}))];
    const clientLabel = (clientOptions.find((option) => option.value === clientId) || clientOptions[0]).label;

    const renderToolbar = () => (
        <div className="alc-mr-toolbar">
            <div className="uiseg alc-filters" role="tablist" aria-label={t('alerts_center.mr.status_label')}>
                {STATUS_FILTERS.map((key) => {
                    const active = key === status;
                    return (
                        <button key={key} type="button" role="tab" aria-selected={active} data-status={key}
                                className={`uiseg__btn${active ? ' -active' : ''}`} onClick={() => setStatus(key)}>
                            {t(STATUS_LABEL_KEYS[key])}
                            <span className="uiseg__count">{counts[key]}</span>
                        </button>
                    );
                })}
            </div>
            {admin && onViewAs && (
                <div className="alc-mr-pick" data-testid="alc-v2-viewas">
                    <span className="alc-mr-pick__label">{t('alerts_center.mr.person_filter')}</span>
                    <ChipMenu slot="viewAs" label={personLabel} buttonClassName="alc-mr-pick__chip"
                              menuLabel={t('alerts_center.mr.person_filter')} disabled={viewAsPending}>
                        {({close}) => (
                            <ChipOptions options={personOptions} value={personValue} close={close} onChoose={choosePerson}/>
                        )}
                    </ChipMenu>
                    {/* Sin las personas del equipo, el menú tiene sólo «Cualquiera» y «Ti»: se dice y se reintenta. */}
                    {teamError && (
                        <span className="alc-hint" data-testid="alc-v2-viewas-team-error">
                            {t('alerts_center.v2.view_as_team_error')}
                            {onRetryTeam && (
                                <>
                                    {' · '}
                                    <button type="button" className="alc-link -button" onClick={onRetryTeam}>{t('alerts_center.retry')}</button>
                                </>
                            )}
                        </span>
                    )}
                </div>
            )}
            {clients.length > 0 && (
                <div className="alc-mr-pick" data-testid="alc-mr-client-filter">
                    <span className="alc-mr-pick__label">{t('alerts_center.mr.client_filter')}</span>
                    <ChipMenu slot="client" label={clientLabel} buttonClassName="alc-mr-pick__chip"
                              menuLabel={t('alerts_center.mr.client_filter')}>
                        {({close}) => (
                            <ChipOptions options={clientOptions} value={clientId} close={close} onChoose={setClientId}/>
                        )}
                    </ChipMenu>
                </div>
            )}
            <span className="alc-mr-toolbar__grow"/>
            {/* Un no admin no ve el webhook: es el canal de la agencia (D12). */}
            {!member && <WebhookStatusInline status={webhookState} canEdit={canEdit} onOpen={() => setDrawerOpen(true)}/>}
        </div>
    );

    const renderAlerts = () => {
        // Mientras llega la lista de la persona elegida, la de antes no se muestra: sus filas son de otra persona, y
        // tocarlas mezclaba las dos vistas (review P-av2-fe-centro-02).
        if (viewAsPending) return <div className="box-loader" data-testid="alc-v2-viewas-loading"><Spinner color="positive"/></div>;
        if (!alerts.length) {
            return (
                <div className="alc-empty -custom">
                    <p>{t(member ? 'alerts_center.v2.alerts_empty_member' : 'alerts_center.v2.alerts_empty')}</p>
                    {canEdit && (
                        <Link to={ALERTS_NEW} className="alc-btn -primary">
                            <Plus size={14} weight="bold"/>
                            {t('alerts_center.cta_new')}
                        </Link>
                    )}
                </div>
            );
        }
        if (!shown.length) {
            return (
                <div className="alc-empty" data-testid="alc-empty-filter">
                    <p>{t('alerts_center.mr.empty_filter')}</p>
                    {filtering && (
                        <button type="button" className="alc-btn -ghost" onClick={clearFilters}>{t('alerts_center.mr.see_all')}</button>
                    )}
                </div>
            );
        }
        return shown.map(renderGroup);
    };

    // Las sugeridas las agrega el admin (D13); sin `viewer` (un backend anterior a la ronda 4), como el admin.
    const showSuggestionsHint = !member && canEdit && !person && suggestions.length > 0;

    return (
        <div className={`alc-rules alc-v2 alc-mr${member ? ' -member' : ''}`} data-testid="alc-v2-list" data-audience={audience}>
            {!canEdit && <p className="alc-notice -warn" role="note" data-testid="alc-read-only">{t('alerts_center.no_edit_permission')}</p>}

            <WatchStrip controls={controls} person={person}/>

            <section className="alc-mr-alerts" aria-labelledby="alc-mr-alerts-title" data-testid="alc-v2-alerts">
                <header className="alc-mr-head">
                    <h2 id="alc-mr-alerts-title" className="alc-mr-head__title">{t('alerts_center.mr.alerts_title')}</h2>
                    <p className="alc-mr-head__lede">{t('alerts_center.mr.alerts_lede')}</p>
                </header>
                {renderToolbar()}
                {renderAlerts()}
            </section>

            {showSuggestionsHint && (
                <div className="alc-mr-hint" data-testid="alc-mr-suggestions-hint">
                    <p>
                        <strong>{t('alerts_center.mr.suggestions_hint_title', {count: suggestions.length})}</strong>
                        {t('alerts_center.mr.suggestions_hint_text')}
                    </p>
                    <Link to={ALERTS_NEW} className="alc-btn -ghost -sm">{t('alerts_center.mr.suggestions_hint_cta')}</Link>
                </div>
            )}

            {!member && (
                <details className="alc-v2-advanced" data-testid="alc-v2-advanced">
                    <summary>{t('alerts_center.v2.advanced')}</summary>
                    <WebhookCardV2 webhook={webhook} systemRules={[]} onSaved={onWebhookSaved} onRetry={onWebhookRetry}
                                   readOnly={!canEdit} onlyChanges sectionId="webhook-advanced"
                                   hiddenEvents={governedEvents} forcedEvents={rowsWithWebhook}/>
                </details>
            )}

            {drawerOpen && (
                <WebhookSetupDrawer pendingRuleTitle={null}
                                    initialUrl={(webhook && webhook.url) || ''} initialDestination={webhookState.destination}
                                    events={knownEvents} onSaved={webhookConnected}
                                    onTested={() => { if (reload) reload(); }} onClose={closeDrawer}/>
            )}
        </div>
    );
}

export default ConvertedRulesList;
